import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { getImageUrl as getImageUrlUtil } from '../../utils/imageUtils';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartMessage, setCartMessage] = useState('');
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  
  // Responsive sizing states
  const [imageHeight, setImageHeight] = useState('160px');
  const [cardPadding, setCardPadding] = useState('1rem 1.25rem');
  const [cardPaddingBottom, setCardPaddingBottom] = useState('1.5rem');
  
  // Get image URL using utility function
  const imageUrl = getImageUrlUtil(product.image);

  // Set responsive sizes based on screen width
  useEffect(() => {
    const updateResponsiveSizes = () => {
      if (window.innerWidth >= 1280) {
        setImageHeight('110px'); // xl screens - smaller for more cards per row
        setCardPadding('0.75rem 1rem');
        setCardPaddingBottom('1.25rem');
      } else if (window.innerWidth >= 960) {
        setImageHeight('130px'); // lg screens
        setCardPadding('1rem 1.125rem');
        setCardPaddingBottom('1.5rem');
      } else if (window.innerWidth >= 600) {
        setImageHeight('150px'); // md screens
        setCardPadding('1rem 1.25rem');
        setCardPaddingBottom('1.5rem');
      } else {
        setImageHeight('160px'); // sm/xs screens
        setCardPadding('1rem 1.25rem');
        setCardPaddingBottom('1.5rem');
      }
    };

    updateResponsiveSizes();
    window.addEventListener('resize', updateResponsiveSizes);
    return () => window.removeEventListener('resize', updateResponsiveSizes);
  }, []);

  const handleAddToCart = async (e) => {
    e.stopPropagation(); // Prevent card click
    
    try {
      setAddingToCart(true);
      setCartMessage('');
      
      const result = await addToCart(product._id, 1);
      
      if (result.success) {
        setCartMessage('Added to cart!');
        setTimeout(() => setCartMessage(''), 2000);
      } else {
        setCartMessage(result.message || 'Failed to add');
        setTimeout(() => setCartMessage(''), 2000);
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      setCartMessage('Error occurred');
      setTimeout(() => setCartMessage(''), 2000);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleCardClick = () => {
    navigate(`/marketplace/product/${product._id}`);
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div 
        style={{
          background: 'var(--mk-surface)',
          borderRadius: '1rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          border: '1px solid var(--mk-border)',
          position: 'relative'
        }}
        onClick={handleCardClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
      }}
    >
      {/* Product Image */}
      <div style={{
        width: '100%',
        height: imageHeight,
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        {imageLoading && !imageError && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            zIndex: 1
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #E2E8F0',
              borderTop: '3px solid #1E40AF',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        )}
        <img
          src={imageUrl}
          alt={product.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.3s ease',
            opacity: imageLoading ? 0 : 1,
            display: imageError ? 'none' : 'block'
          }}
          onLoad={() => {
            console.log('ProductCard: Image loaded successfully:', imageUrl);
            setImageLoading(false);
            setImageError(false);
          }}
          onError={(e) => {
            console.error('ProductCard: Image failed to load:', imageUrl, e);
            setImageError(true);
            setImageLoading(false);
          }}
        />
        {imageError && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
          }}>
            <img
              src="/placeholder-product.png"
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
        
        {/* Category Badge */}
        <div style={{
          position: 'absolute',
          top: '0.75rem',
          left: '0.75rem',
          background: product.category === 'medicine' 
            ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
            : 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
          color: 'white',
          padding: '0.35rem 0.75rem',
          borderRadius: '1.5rem',
          fontSize: '0.7rem',
          fontWeight: '600',
          boxShadow: '0 2px 10px rgba(30, 64, 175, 0.3)'
        }}>
          {product.category === 'medicine' ? '💊 Medicine' : '🧪 Lab Test'}
        </div>

        {/* Stock Status Badge */}
        {product.stock === 0 && (
          <div style={{
            position: 'absolute',
            top: '0.75rem',
            right: '0.75rem',
            background: 'rgba(100, 116, 139, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            padding: '0.35rem 0.9rem',
            borderRadius: '1.5rem',
            fontSize: '0.7rem',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)'
          }}>
            Out of Stock
          </div>
        )}
      </div>

      {/* Product Info - Responsive padding */}
      <div style={{ padding: cardPadding, paddingBottom: cardPaddingBottom }}>
        {/* Product Name */}
        <h3 style={{
          fontSize: '1rem',
          fontWeight: '650',
          color: 'var(--mk-text)',
          marginBottom: '0.5rem',
          lineHeight: '1.3',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          letterSpacing: '-0.01em'
        }}>
          {product.name}
        </h3>

        {/* Product Description */}
        <p style={{
          fontSize: '0.8rem',
          color: 'var(--mk-muted)',
          marginBottom: '0.75rem',
          lineHeight: '1.4',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {product.description}
        </p>

        {/* Additional Info */}
        {product.category === 'medicine' && product.manufacturer && (
          <p style={{
            fontSize: '0.7rem',
            color: 'var(--mk-muted)',
            marginBottom: '0.5rem',
            fontStyle: 'italic'
          }}>
            by {product.manufacturer}
          </p>
        )}

        {/* Prescription Required Badge */}
        {product.prescriptionRequired && (
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{
              background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              color: 'white',
              padding: '0.25rem 0.7rem',
              borderRadius: '0.75rem',
              fontSize: '0.7rem',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(30, 64, 175, 0.2)'
            }}>
              📋 Prescription Required
            </span>
          </div>
        )}

        {/* Price and Stock */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <div style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            ৳{product.price}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: product.stock > 0 ? '#1E40AF' : '#64748B',
            fontWeight: '500'
          }}>
            {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
          </div>
        </div>

        {/* Lab Options for Lab Tests */}
        {product.category === 'lab-test' && product.labOptions && product.labOptions.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#1E293B',
              marginBottom: '0.5rem'
            }}>
              Available at:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {product.labOptions.slice(0, 2).map((option, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0.75rem',
                  background: 'linear-gradient(135deg, rgba(219, 234, 254, 0.3) 0%, rgba(240, 249, 255, 0.5) 100%)',
                  borderRadius: '0.5rem',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 3px rgba(30, 64, 175, 0.08)'
                }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '500',
                    color: '#1E293B'
                  }}>
                    {option.labName}
                  </span>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    color: '#1E40AF'
                  }}>
                    ৳{option.price}
                  </span>
                </div>
              ))}
              {product.labOptions.length > 2 && (
                <div style={{
                  fontSize: '0.7rem',
                  color: '#64748B',
                  textAlign: 'center',
                  padding: '0.2rem'
                }}>
                  +{product.labOptions.length - 2} more options
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add to Cart Button */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={handleAddToCart}
            disabled={addingToCart || product.stock === 0}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '0.75rem',
              fontWeight: '600',
              fontSize: '0.875rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: 'none',
              cursor: product.stock === 0 ? 'not-allowed' : addingToCart ? 'wait' : 'pointer',
              background: product.stock === 0
                ? '#E2E8F0'
                : addingToCart
                ? 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              color: product.stock === 0 ? '#94A3B8' : 'white',
              boxShadow: product.stock === 0 ? 'none' : '0 4px 16px rgba(30, 64, 175, 0.3)',
              letterSpacing: '0.01em'
            }}
            onMouseEnter={(e) => {
              if (product.stock > 0 && !addingToCart) {
                e.target.style.transform = 'scale(1.02)';
                e.target.style.boxShadow = '0 6px 20px rgba(30, 64, 175, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (product.stock > 0 && !addingToCart) {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(30, 64, 175, 0.3)';
              }
            }}
          >
            {product.stock === 0 
              ? 'Out of Stock' 
              : addingToCart 
              ? 'Adding...' 
              : 'Add to Cart'
            }
          </button>

          {/* Cart Message */}
          {cartMessage && (
            <div 
              onClick={(e) => e.stopPropagation()} // Prevent card click when clicking message
              style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                marginTop: '0.5rem',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                textAlign: 'center',
                fontWeight: '500',
                background: cartMessage.includes('Added') 
                  ? 'linear-gradient(135deg, rgba(219, 234, 254, 0.8) 0%, rgba(191, 219, 254, 0.6) 100%)'
                  : 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(219, 234, 254, 0.5) 100%)',
                color: cartMessage.includes('Added') ? '#1E40AF' : '#64748B',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                animation: 'fadeInUp 0.3s ease',
                zIndex: 1000, // Ensure it appears above other elements
                border: '1px solid rgba(0, 0, 0, 0.1)',
                cursor: 'default' // Show it's not clickable
              }}
            >
              {cartMessage}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ProductCard;
